/**
 * Where the session lives in a browser-only app.
 *
 * The ACCESS token stays in memory: it is short-lived, and a token that never
 * touches storage cannot be read out of it. The REFRESH token is persisted so
 * a reload does not mean a login — that is the one thing storage is for here.
 * Both are wiped together; there is no state with one and not the other.
 */

const REFRESH_KEY = 'da.refresh';

let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

/** True when a reload could resume the session — not proof that it is still good. */
export function hasSession(): boolean {
  return getRefreshToken() !== null;
}

export function setTokens(pair: { accessToken: string; refreshToken: string }): void {
  accessToken = pair.accessToken;
  try {
    localStorage.setItem(REFRESH_KEY, pair.refreshToken);
  } catch {
    // Private mode with storage blocked: the session lasts until the tab closes.
  }
}

export function clearTokens(): void {
  accessToken = null;
  try {
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    // nothing to clear
  }
}

// ── "The session is over" — one event, many listeners ─────────────────────────

type Listener = () => void;
const listeners = new Set<Listener>();

/** Subscribe to the moment the session becomes unrecoverable; returns the unsubscribe. */
export function onAuthExpired(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function emitAuthExpired(): void {
  for (const l of listeners) l();
}
